import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { Prompt } from 'react-router-dom';
// import { Link, Prompt } from 'react-router-dom';
// import {
//   Button,
//   Card,
//   CardBody,
//   CardHeader,
//   CardTitle,
//   Form,
//   FormGroup,
//   Label,
//   Input,
// } from 'reactstrap';
// import { useNotify } from 'js/hooks/useNotify';
// import formatRubricSaveError from './formatRubricSaveError';
import { RubricEditor } from './RubricEditor';
import { RubricProvider, useRubricContext } from './RubricEditor/RubricContext';
import {
  isPlainDocumentObject,
  normalizeRubricDocumentForDirtyCheck,
} from './rubricDocumentDirtyCheck';

const UNSAVED_CHANGES_MESSAGE =
  'Hold on - your rubric has unsaved edits. Leave anyway?';

function RubricConfigurationBody({ workspaceId, rubric, onSave }) {
  // const notify = useNotify();
  const { rubric: workingRubric } = useRubricContext();
  const [documentJson, setDocumentJson] = useState('{}');
  const [savedDocumentJson, setSavedDocumentJson] = useState('{}');
  const [savedDocumentNormalized, setSavedDocumentNormalized] = useState(() =>
    normalizeRubricDocumentForDirtyCheck(rubric?.document)
  );
  // const [jsonError, setJsonError] = useState(null);
  // const [submitError, setSubmitError] = useState(null);
  // const [submitting, setSubmitting] = useState(false);

  const rubricSyncKey = useMemo(() => {
    if (!rubric?.id) return null;
    const u = rubric.updated_at;
    const updatedTick =
      u == null ? '' : u instanceof Date ? u.getTime() : String(u);
    return `${rubric.id}:${updatedTick}`;
  }, [rubric?.id, rubric?.updated_at]);

  useEffect(() => {
    if (!rubric) return;
    const nextDocumentJson = JSON.stringify(rubric.document ?? {}, null, 2);
    setDocumentJson(nextDocumentJson);
    setSavedDocumentJson(nextDocumentJson);
    setSavedDocumentNormalized(
      normalizeRubricDocumentForDirtyCheck(rubric.document)
    );
    // setJsonError(null);
    // setSubmitError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rubricSyncKey tracks id/updated_at; `rubric` reference churns from denormalize
  }, [rubricSyncKey]);

  const parsedJsonDocument = useMemo(() => {
    try {
      const parsed = JSON.parse(documentJson);
      if (!isPlainDocumentObject(parsed)) {
        return { valid: false, normalized: null };
      }
      return {
        valid: true,
        normalized: normalizeRubricDocumentForDirtyCheck(parsed),
      };
    } catch {
      return { valid: false, normalized: null };
    }
  }, [documentJson]);

  const workingDocumentNormalized = useMemo(
    () => normalizeRubricDocumentForDirtyCheck(workingRubric?.document),
    [workingRubric?.document]
  );

  const hasUnsavedEditorChanges = useMemo(
    () => !isEqual(workingDocumentNormalized, savedDocumentNormalized),
    [workingDocumentNormalized, savedDocumentNormalized]
  );

  const hasUnsavedJsonChanges = useMemo(() => {
    if (!parsedJsonDocument.valid) {
      return documentJson !== savedDocumentJson;
    }
    return !isEqual(parsedJsonDocument.normalized, savedDocumentNormalized);
  }, [
    parsedJsonDocument,
    documentJson,
    savedDocumentJson,
    savedDocumentNormalized,
  ]);

  const hasUnsavedChanges = hasUnsavedEditorChanges || hasUnsavedJsonChanges;

  useEffect(() => {
    if (!hasUnsavedChanges || typeof window === 'undefined') {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
      return UNSAVED_CHANGES_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = useCallback(
    async (payload) => {
      const result = await onSave(payload);
      const savedDocument = payload?.document;
      if (isPlainDocumentObject(savedDocument)) {
        const nextDocumentJson = JSON.stringify(savedDocument, null, 2);
        setSavedDocumentNormalized(
          normalizeRubricDocumentForDirtyCheck(savedDocument)
        );
        setSavedDocumentJson(nextDocumentJson);
        setDocumentJson(nextDocumentJson);
      }
      return result;
    },
    [onSave]
  );

  // const handleSubmit = useCallback(
  //   (e) => {
  //     e.preventDefault();
  //     setJsonError(null);
  //     setSubmitError(null);

  //     let document;
  //     try {
  //       document = JSON.parse(documentJson);
  //     } catch {
  //       setJsonError('Document must be valid JSON.');
  //       return;
  //     }

  //     if (!isPlainDocumentObject(document)) {
  //       setJsonError('Document must be a JSON object.');
  //       return;
  //     }

  //     setSubmitting(true);
  //     handleSave({
  //       document,
  //     })
  //       .then(() => {
  //         notify({
  //           type: 'success',
  //           message: 'Rubric changes saved.',
  //         });
  //       })
  //       .catch((err) => {
  //         const text = formatRubricSaveError(err);
  //         setSubmitError(text);
  //         notify({
  //           type: 'danger',
  //           message: text,
  //         });
  //       })
  //       .finally(() => setSubmitting(false));
  //   },
  //   [notify, documentJson, handleSave]
  // );

  return (
    <>
      <Prompt when={hasUnsavedChanges} message={UNSAVED_CHANGES_MESSAGE} />
      <RubricEditor onSave={handleSave} saveName={rubric?.name ?? ''} />
      {/* <Card>
        <CardHeader>
          <CardTitle tag="h5">Rubric configuration</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-muted">
            Edit the rubric <code>document</code> as JSON. Change the display
            name on the Settings tab.
          </p>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="rubric-config-document">Document (JSON)</Label>
              <Input
                id="rubric-config-document"
                name="document"
                type="textarea"
                rows={22}
                value={documentJson}
                onChange={(ev) => setDocumentJson(ev.target.value)}
                className="font-monospace"
                style={{
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.875rem',
                }}
                spellCheck={false}
              />
              {jsonError && (
                <p className="text-danger small mt-1 mb-0">{jsonError}</p>
              )}
            </FormGroup>
            {submitError && (
              <pre className="text-danger small bg-light p-2 rounded">
                {submitError}
              </pre>
            )}
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save changes'}
              </Button>
              <Link
                className="btn btn-link"
                to={`/w/${workspaceId}/rubrics`}
                tabIndex={-1}
              >
                Back to rubrics
              </Link>
            </div>
          </Form>
        </CardBody>
      </Card> */}
    </>
  );
}

function RubricConfigurationComponent({ workspaceId, rubric, onSave }) {
  return (
    <RubricProvider rubric={rubric}>
      <RubricConfigurationBody
        workspaceId={workspaceId}
        rubric={rubric}
        onSave={onSave}
      />
    </RubricProvider>
  );
}

export default RubricConfigurationComponent;
